import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  clearAssistantHistory,
  createAssistantConversation,
  deleteAssistantConversation,
  getAssistantConversations,
  getAssistantHistory,
  renameAssistantConversation,
  sendAssistantMessage,
} from '@/api/assistant';
import { AiLoading } from '@/components/ui/ai-loading/ai-loading';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { useAuthStore } from '@/stores/authStore';
import type { AssistantConversation, AssistantMessage } from '@/types';

import styles from './assistant.module.css';

function ConversationRenameInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <input
      ref={inputRef}
      type="text"
      className={styles.renameInput}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

const TEACHER_QUICK_QUESTIONS = [
  '帮我设计一份初二勾股定理作业的评分维度',
  '怎么处理学生对 AI 判分的申诉？',
  '班级分析看板里的薄弱知识点热力图怎么看？',
  '帮我写一段给中等生的作业评语',
];

const STUDENT_QUICK_QUESTIONS = [
  '帮我制定一份期中考前一周的复习计划',
  '一元一次方程应用题总是列错方程，怎么破？',
  '错题本里的"重做"功能怎么用效果最好？',
  '怎么针对扣分点向 AI 追问？',
];

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

export function AssistantPage() {
  const user = useAuthStore((state) => state.user);
  const isTeacher = user?.role === 'teacher';
  const queryClient = useQueryClient();

  const [activeId, setActiveId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [mockTipVisible, setMockTipVisible] = useState(false);
  const [error, setError] = useState('');
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const { data: conversations, isLoading: conversationsLoading } = useQuery({
    queryKey: ['assistant', 'conversations', user?.id],
    queryFn: getAssistantConversations,
    enabled: !!user?.id,
  });

  const activeConversation = useMemo(
    () => conversations?.find((c) => c.id === activeId) ?? null,
    [conversations, activeId],
  );

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['assistant', 'history', activeId, user?.id],
    queryFn: () => getAssistantHistory(activeId!),
    enabled: activeId !== null && activeId !== undefined && activeId > 0,
  });

  const messages = history ?? [];
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // 没有选中会话时，默认选中第一个；没有会话时自动新建
  useEffect(() => {
    if (conversationsLoading) return;
    if (conversations && conversations.length > 0) {
      const exists = conversations.some((c) => c.id === activeId);
      if (!exists) {
        setActiveId(conversations[0].id);
      }
    }
  }, [conversations, conversationsLoading, activeId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sending]);

  useEffect(() => {
    if (renamingId !== null) {
      renameInputRef.current?.focus();
    }
  }, [renamingId]);

  const createConversationMutation = useMutation({
    mutationFn: (title?: string) => createAssistantConversation(title),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['assistant', 'conversations', user?.id],
      });
      setActiveId(data.id);
    },
    onError: () => {
      setError('创建会话失败，请重试');
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) =>
      renameAssistantConversation(id, title),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assistant', 'conversations', user?.id],
      });
      setRenamingId(null);
    },
    onError: () => {
      setError('重命名失败，请重试');
      setRenamingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteAssistantConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assistant', 'conversations', user?.id],
      });
      setActiveId(null);
    },
    onError: () => {
      setError('删除会话失败，请重试');
    },
  });

  const handleStartRename = (conv: AssistantConversation) => {
    setRenamingId(conv.id);
    setRenameValue(conv.title);
  };

  const handleConfirmRename = (id: number) => {
    const title = renameValue.trim();
    if (!title) {
      setRenamingId(null);
      return;
    }
    renameMutation.mutate({ id, title });
  };

  const handleSend = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;

    setSending(true);
    setError('');
    setInput('');
    setMockTipVisible(false);

    let conversationId = activeId ?? undefined;

    // 乐观追加用户消息
    const optimistic: AssistantMessage = {
      id: -Date.now(),
      role: 'user',
      content: message,
      persona: isTeacher ? 'teacher' : 'student',
      conversation_id: activeId ?? 0,
      created_at: new Date().toISOString(),
    };
    if (activeId !== null) {
      queryClient.setQueryData<AssistantMessage[]>(
        ['assistant', 'history', activeId, user?.id],
        (old) => [...(old ?? []), optimistic],
      );
    }

    try {
      const result = await sendAssistantMessage(message, conversationId);

      // 如果后端自动新建了会话，同步激活
      if (activeId !== result.conversation_id) {
        setActiveId(result.conversation_id);
        queryClient.invalidateQueries({
          queryKey: ['assistant', 'conversations', user?.id],
        });
      }

      queryClient.setQueryData<AssistantMessage[]>(
        ['assistant', 'history', result.conversation_id, user?.id],
        (old) => {
          const filtered = (old ?? []).filter((m) => m.id > 0);
          return [...filtered, result.user_message, result.assistant_message];
        },
      );
      setMockTipVisible(result.mock);
    } catch (err) {
      setError(err instanceof Error ? err.message : '发送失败，请重试');
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    if (activeId === null) return;
    try {
      await clearAssistantHistory(activeId);
      queryClient.setQueryData<AssistantMessage[]>(
        ['assistant', 'history', activeId, user?.id],
        [],
      );
      queryClient.invalidateQueries({
        queryKey: ['assistant', 'history', activeId, user?.id],
      });
    } catch {
      setError('清空失败，请重试');
    }
  };

  const handleNewConversation = () => {
    createConversationMutation.mutate(undefined);
  };

  const quickQuestions = isTeacher ? TEACHER_QUICK_QUESTIONS : STUDENT_QUICK_QUESTIONS;

  return (
    <div className={styles.pageWithSidebar}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarTitle}>会话列表</span>
          <Button size="small" variant="secondary" onClick={handleNewConversation}>
            新建会话
          </Button>
        </div>
        <div className={styles.conversationList}>
          {conversationsLoading && (
            <div className={styles.sidebarEmpty}>加载中…</div>
          )}
          {!conversationsLoading && (!conversations || conversations.length === 0) && (
            <div className={styles.sidebarEmpty}>
              暂无会话
              <button
                type="button"
                className={styles.newLink}
                onClick={handleNewConversation}
              >
                开始新对话
              </button>
            </div>
          )}
          {conversations?.map((conv) => (
            <div
              key={conv.id}
              className={`${styles.conversationItem} ${
                conv.id === activeId ? styles.conversationActive : ''
              }`}
              onClick={() => conv.id !== renamingId && setActiveId(conv.id)}
            >
              {renamingId === conv.id ? (
                <ConversationRenameInput
                  inputRef={renameInputRef}
                  value={renameValue}
                  onChange={setRenameValue}
                  onBlur={() => handleConfirmRename(conv.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleConfirmRename(conv.id);
                    } else if (e.key === 'Escape') {
                      setRenamingId(null);
                    }
                  }}
                />
              ) : (
                <>
                  <span
                    className={styles.conversationTitle}
                    onDoubleClick={() => handleStartRename(conv)}
                    title="双击重命名"
                  >
                    {conv.title}
                  </span>
                  <button
                    type="button"
                    className={styles.renameBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartRename(conv);
                    }}
                    title="重命名"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`确定删除会话「${conv.title}」吗？`)) {
                        deleteMutation.mutate(conv.id);
                      }
                    }}
                    title="删除"
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.header}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>
              {isTeacher ? '教师 AI 助手' : 'AI 学习助手'}
            </h1>
            {activeConversation && (
              <span className={styles.activeTitle}>{activeConversation.title}</span>
            )}
          </div>
          <p className={styles.subtitle}>
            {isTeacher
              ? '熟悉作业管理、班级管理、批阅复核、班级分析与申诉处理，帮你出题、设计评分维度、写评语、解读学情。每次对话都会自动保存。'
              : '熟悉作业提交、错题本、学情报告与待办中心，帮你讲解错题、制定复习计划、用好平台功能。每次对话都会自动保存。'}
          </p>
        </header>

        <Card className={styles.chatCard}>
          <div className={styles.messages}>
            {!activeId && (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>{isTeacher ? '🤖' : '🎓'}</div>
                <div className={styles.emptyTitle}>
                  还没有会话
                </div>
                <div className={styles.emptyHint}>
                  点击左侧「新建会话」开始一段新对话，所有聊天记录都会自动保存。
                </div>
                <button
                  type="button"
                  className={styles.quickBtn}
                  onClick={handleNewConversation}
                >
                  新建会话
                </button>
              </div>
            )}

            {activeId && historyLoading && (
              <div className={styles.sidebarEmpty}>加载中…</div>
            )}

            {activeId && !historyLoading && messages.length === 0 && !sending && (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>{isTeacher ? '🤖' : '🎓'}</div>
                <div className={styles.emptyTitle}>
                  {isTeacher
                    ? `你好，${user?.display_name ?? '老师'}！我是你的教学助手`
                    : `你好，${user?.display_name ?? '同学'}！我是你的学习助手`}
                </div>
                <div className={styles.emptyHint}>
                  {isTeacher
                    ? '可以问我平台功能怎么用，也可以让我帮你出题、设计评分维度、写评语或分析班级学情。'
                    : '可以问我学习问题或平台功能，也可以让我讲解知识点、分析错题或制定复习计划。'}
                </div>
                <div className={styles.quickRow}>
                  {quickQuestions.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className={styles.quickBtn}
                      onClick={() => handleSend(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`${styles.row} ${msg.role === 'user' ? styles.rowUser : ''}`}
              >
                <span
                  className={`${styles.avatar} ${
                    msg.role === 'user' ? styles.avatarUser : styles.avatarBot
                  }`}
                >
                  {msg.role === 'user'
                    ? (user?.display_name?.[0] ?? '我')
                    : isTeacher
                      ? '教'
                      : '学'}
                </span>
                <div>
                  <div
                    className={`${styles.bubble} ${
                      msg.role === 'user' ? styles.bubbleUser : styles.bubbleBot
                    }`}
                  >
                    {msg.content}
                  </div>
                  <span className={styles.msgTime}>{formatTime(msg.created_at)}</span>
                </div>
              </div>
            ))}

            {sending && (
              <div className={styles.row}>
                <span className={`${styles.avatar} ${styles.avatarBot}`}>
                  {isTeacher ? '教' : '学'}
                </span>
                <div className={`${styles.bubble} ${styles.bubbleBot}`}>
                  <AiLoading compact title="AI 正在思考" hint={['正在理解你的问题…', '正在组织回答…']} />
                </div>
              </div>
            )}

            {mockTipVisible && (
              <div className={styles.mockTip}>
                当前未配置 AI Key，显示的是模拟回复。可在「设置页」中配置个人 API Key 启用真实 AI。
              </div>
            )}
            {error && <div className={styles.mockTip}>{error}</div>}
            <div ref={messagesEndRef} />
          </div>

          <div className={styles.toolRow}>
            <span className={styles.toolHint}>
              {activeId
                ? `已保存 ${messages.filter((m) => m.id > 0).length} 条对话记录`
                : '未选择会话'}
            </span>
            {activeId && messages.length > 0 && (
              <button type="button" className={styles.clearBtn} onClick={handleClear}>
                清空当前会话
              </button>
            )}
          </div>

          <div className={styles.inputBar}>
            <textarea
              className={styles.input}
              value={input}
              rows={1}
              disabled={!activeId}
              placeholder={
                activeId
                  ? isTeacher
                    ? '问我平台功能、让我出题 / 设计评分维度 / 写评语…'
                    : '问我学习问题或平台功能，Enter 发送，Shift+Enter 换行'
                  : '请先新建或选择一个会话'
              }
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button loading={sending} onClick={() => handleSend()} disabled={!activeId}>
              发送
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
